import Tooltip from '@mui/material/Tooltip';
import FormatBoldIcon from 'ui/elements/icons/BoldIcon';
import FormatItalicIcon from 'ui/elements/icons/ItalicIcon';
import BulletIcon from 'ui/elements/icons/ListIcon';
import ListIcon from 'ui/elements/icons/ListNumberedIcon';
import FormatQuoteIcon from 'ui/elements/icons/QuoteIcon';
import FormatUnderlinedIcon from 'ui/elements/icons/UnderlineIcon';
import InserLinkIcon from 'ui/elements/icons/LinkIcon';
import InsertPhotoIcon from 'ui/elements/icons/ImageIcon';
import LibraryAddIcon from 'ui/elements/icons/CodeIcon';
import React, { useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Quill } from 'react-quill';
import styles from '../quill.scss';
import { Hidden } from '@mui/material';
import { halfSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import SettingsIcon from 'ui/elements/icons/SettingsIcon';
import styled from '@emotion/styled';
import ProfileAddIcon from 'ui/elements/icons/ProfileAddIcon';
import UpdateIcon from 'ui/elements/icons/UpdateIcon';
import ChartIcon from 'ui/elements/icons/ChartIcon';

const icons = Quill.import('ui/icons');
icons['bold'] = ReactDOMServer.renderToString(<FormatBoldIcon />);
icons['italic'] = ReactDOMServer.renderToString(<FormatItalicIcon />);
icons['underline'] = ReactDOMServer.renderToString(<FormatUnderlinedIcon />);
icons['custom-blockquote'] = ReactDOMServer.renderToString(<FormatQuoteIcon />);
icons['list']['bullet'] = ReactDOMServer.renderToString(<BulletIcon />);
icons['list']['ordered'] = ReactDOMServer.renderToString(<ListIcon />);
icons['link'] = ReactDOMServer.renderToString(<InserLinkIcon />);
icons['image'] = ReactDOMServer.renderToString(<InsertPhotoIcon />);
icons['iframely'] = ReactDOMServer.renderToString(<LibraryAddIcon />);
icons['companyProfiles'] = ReactDOMServer.renderToString(<LibraryAddIcon />);

const TypographyTools = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  ${bluePlanetTheme.breakpoints.down('sm')} {
    width: calc(100% - 30px);
  }
`;

export function AdvancedToolbar({
  desktopActions,
  showKpiButton = true,
}: {
  desktopActions?: React.ReactNode;
  showKpiButton?: boolean;
}) {
  const [showFullMenu, setShowFullMenu] = useState(false);

  return (
    <div id="toolbar" className={styles.stickyToolbar}>
      <TypographyTools>
        <div className="ql-formats">
          <select tabIndex={1} title="Header" className="ql-header" defaultValue="" onChange={e => e.persist()}>
            <option value="1" />
            <option value="2" />
            <option />
          </select>
        </div>
        <div className="ql-formats">
          <Tooltip title="Bold">
            <button tabIndex={1} type="button" className="ql-bold">
              <FormatBoldIcon />
            </button>
          </Tooltip>
          <Tooltip title="Italic" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-italic">
              <FormatItalicIcon />
            </button>
          </Tooltip>
          <Tooltip title="Underline" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-underline">
              <FormatUnderlinedIcon />
            </button>
          </Tooltip>
          <Tooltip title="Quote" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-custom-blockquote">
              <FormatQuoteIcon />
            </button>
          </Tooltip>
        </div>
        <div className="ql-formats">
          <Tooltip title="Bullet list">
            <button tabIndex={1} type="button" className="ql-list" value="bullet">
              <BulletIcon />
            </button>
          </Tooltip>
          <Tooltip title="Numbered list" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-list" value="ordered">
              <ListIcon />
            </button>
          </Tooltip>
        </div>
        <div className="ql-formats">
          <Tooltip title="Insert link" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-link">
              <InserLinkIcon />
            </button>
          </Tooltip>
          <Tooltip title="Insert picture">
            <button tabIndex={1} type="button" className="ql-image">
              <InsertPhotoIcon />
            </button>
          </Tooltip>
          <Tooltip title="Embed content" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-iframely" data-intercom-target="embed-content">
              <LibraryAddIcon />
            </button>
          </Tooltip>
          <Tooltip title="Embed profiles" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button
              tabIndex={1}
              type="button"
              className="ql-company-profiles"
              data-intercom-target="embed-company-profiles"
            >
              <ProfileAddIcon />
            </button>
          </Tooltip>
          <Tooltip title="Embed update" className={showFullMenu ? '' : 'u-mobile-up-only'}>
            <button tabIndex={1} type="button" className="ql-post-embed">
              <UpdateIcon />
            </button>
          </Tooltip>
          {showKpiButton && (
            <Tooltip title="Embed KPI" className={showFullMenu ? '' : 'u-mobile-up-only'}>
              <button tabIndex={1} type="button" className="ql-kpi-embed" data-intercom-target="embed-kpi">
                <ChartIcon />
              </button>
            </Tooltip>
          )}
        </div>
      </TypographyTools>
      <Hidden smUp>
        <button tabIndex={1} type="button" onClick={() => setShowFullMenu(!showFullMenu)}>
          <SettingsIcon />
        </button>
      </Hidden>
      <Hidden smDown>
        <div
          style={{
            marginLeft: 'auto',
            padding: `0 ${halfSpacing}`,
            borderLeft: `1px solid ${bluePlanetTheme.bluePlanetPalette.white}`,
            height: 50, // To get the border to go all the way from top to bottom
          }}
          className="u-flex u-flex-align-center"
        >
          {desktopActions}
        </div>
      </Hidden>
    </div>
  );
}
